import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Printer, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);

export default function PublicQuote() {
  const { slug } = useParams();
  const [q, setQ] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [accepted, setAccepted] = useState(null); // {invoice_slug, invoice_number}

  const load = () => axios.get(`${BACKEND}/api/public/quote/${slug}`).then((r) => setQ(r.data)).catch(() => setError("Quotation not found or link expired."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{error}</div>;
  if (!q) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading quotation…</div>;

  const profile = q.billing_profile_snapshot || {};
  const accent = profile.accent_color || "#0f172a";
  const logoUri = profile.logo_data_uri || null;
  const isLocked = ["converted", "rejected", "expired"].includes(q.status);
  const isAccepted = q.status === "accepted" || q.status === "converted";

  const doAccept = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/public/quote/${slug}/accept`, { signer_name: signerName });
      setAccepted(r.data);
      setAcceptOpen(false);
      // Celebrate
      const fire = (x) => confetti({ particleCount: 90, spread: 65, startVelocity: 45, origin: { x, y: 0.6 }, colors: ["#25D366", "#38BDF8", "#FBBF24", "#A78BFA"] });
      fire(0.2); setTimeout(() => fire(0.8), 180);
      toast.success(`Accepted — invoice ${r.data.invoice_number} issued.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Accept failed");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND}/api/public/quote/${slug}/reject`, { reason: rejectReason });
      setRejectOpen(false);
      toast.success("Quotation rejected.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-black" data-testid="public-quote">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 15mm 12mm; size: A4; }
          body { background: white !important; }
        }
        .quote-page { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1a1a1a; }
      `}</style>

      <div className="h-1.5 w-full" style={{ background: accent }} data-testid="quote-accent-ribbon" />

      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="text-sm">
          <span className="font-bold">{q.quote_number}</span> · {profile.company_name || "Quotation"}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => window.open(`${BACKEND}/api/public/quote/${slug}/pdf`, "_blank")} data-testid="public-quote-download-pdf">
            <Printer className="h-4 w-4 mr-2" />Download PDF
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(window.location.href).then(() => toast.success("URL copied")).catch(() => toast.error("Copy failed")); }} data-testid="public-quote-copy-url">
            <Copy className="h-4 w-4 mr-2" />Copy link
          </Button>
        </div>
      </div>

      {/* Accept / Reject action banner */}
      <div className="no-print max-w-4xl mx-auto p-4">
        {isAccepted && (accepted || q.converted_invoice_number) && (
          <div className="rounded-sm border border-green-500/40 bg-green-500/5 p-4 text-sm" data-testid="public-quote-accepted-banner">
            <div className="font-bold text-green-700">Quotation accepted — thank you!</div>
            <div className="text-slate-700 mt-1">
              Invoice <b>{accepted?.invoice_number || q.converted_invoice_number}</b> has been issued.
              {accepted?.invoice_slug || q.converted_invoice_id ? (
                <> Pay online: <a href={`/inv/${accepted?.invoice_slug || ""}`} className="text-primary underline" data-testid="public-quote-pay-link">Open invoice</a></>
              ) : null}
            </div>
          </div>
        )}
        {q.status === "rejected" && (
          <div className="rounded-sm border border-red-500/40 bg-red-500/5 p-4 text-sm" data-testid="public-quote-rejected-banner">
            <div className="font-bold text-red-700">This quotation has been rejected.</div>
          </div>
        )}
        {!isLocked && q.status !== "accepted" && (
          <div className="flex flex-wrap gap-3">
            <Button className="rounded-sm bg-green-600 hover:bg-green-700 text-white flex-1 h-12" onClick={() => setAcceptOpen(true)} data-testid="public-quote-accept">
              <Check className="h-4 w-4 mr-2" /> Accept quotation
            </Button>
            <Button variant="outline" className="rounded-sm border-red-300 text-red-600 hover:bg-red-50 h-12" onClick={() => setRejectOpen(true)} data-testid="public-quote-reject">
              <X className="h-4 w-4 mr-2" /> Reject
            </Button>
          </div>
        )}
      </div>

      <div className="quote-page max-w-4xl mx-auto p-8 md:p-12">
        {/* Header */}
        <div className="flex justify-between items-start mb-10 pb-6 border-b-2" style={{ borderColor: accent }}>
          <div className="flex items-start gap-4">
            {logoUri && (
              <img
                src={logoUri}
                alt="Company logo"
                className="h-16 w-16 object-contain rounded-sm bg-white border border-slate-200 p-1.5 shrink-0"
                data-testid="quote-logo"
              />
            )}
            <div>
              <h1 className="text-3xl font-black tracking-tight" data-testid="quote-company-name">{profile.company_name || profile.division_name || "Company Name"}</h1>
              {profile.company_name && profile.division_name && profile.company_name !== profile.division_name && (
                <div className="text-sm text-slate-600 font-semibold mt-0.5" data-testid="quote-division-name">{profile.division_name}</div>
              )}
              {profile.uen && <div className="text-xs text-slate-500 mt-1">UEN: {profile.uen}</div>}
              <div className="text-sm mt-3 whitespace-pre-line leading-snug text-slate-700">
                {profile.address}{profile.postal_code ? `, Singapore ${profile.postal_code}` : ""}<br />
                {profile.phone && <>Tel: {profile.phone}<br /></>}
                {profile.email && <>Email: {profile.email}<br /></>}
                {profile.website && <>{profile.website}</>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black tracking-tight" style={{ color: accent }}>QUOTATION</div>
            <div className="text-sm mt-1"># <span className="font-mono font-bold">{q.quote_number}</span></div>
            <div className="text-sm mt-3 text-slate-600">
              <div><span className="text-slate-400 uppercase text-[10px] tracking-widest">Issue date</span><br />{q.issue_date}</div>
              {q.valid_until && <div className="mt-2"><span className="text-slate-400 uppercase text-[10px] tracking-widest">Valid until</span><br />{q.valid_until}</div>}
            </div>
          </div>
        </div>

        {/* Bill to */}
        <div className="flex justify-between mb-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Bill to</div>
            <div className="font-bold text-lg mt-1">{q.bill_to?.name}</div>
            <div className="text-sm text-slate-700">
              {q.bill_to?.address}{q.bill_to?.postal_code ? `, Singapore ${q.bill_to.postal_code}` : ""}<br />
              {q.bill_to?.phone}<br />
              {q.bill_to?.email}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Total quoted ({q.currency})</div>
            <div className="text-3xl font-black tracking-tight mt-1">{fmt(q.total, q.currency)}</div>
          </div>
        </div>

        {/* Line items */}
        <table className="w-full text-sm mb-6">
          <thead>
            <tr className="border-b border-slate-300 text-[10px] uppercase tracking-widest text-slate-500">
              <th className="text-left py-2">Description</th>
              <th className="text-right py-2 w-16">Qty</th>
              <th className="text-right py-2 w-28">Price</th>
              <th className="text-right py-2 w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(q.line_items || []).map((li, i) => (
              <tr key={i} className="border-b border-slate-100 align-top">
                <td className="py-3">{li.description}{li.include_gst && <span className="text-[9px] text-slate-400 ml-2">+GST</span>}</td>
                <td className="py-3 text-right">{li.quantity}</td>
                <td className="py-3 text-right">{fmt(li.price, q.currency)}</td>
                <td className="py-3 text-right font-semibold">{fmt(li.amount, q.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals right-aligned */}
        <div className="flex justify-end mb-8">
          <div className="w-72 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{fmt(q.subtotal, q.currency)}</span></div>
            {q.service_fee_amount ? <div className="flex justify-between"><span className="text-slate-500">Service fee ({(q.service_fee_pct || 0)}%)</span><span>{fmt(q.service_fee_amount, q.currency)}</span></div> : null}
            {q.gst_amount ? <div className="flex justify-between"><span className="text-slate-500">GST ({((q.gst_rate || 0) * 100).toFixed(1)}%)</span><span>{fmt(q.gst_amount, q.currency)}</span></div> : null}
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-300"><span>Total</span><span>{fmt(q.total, q.currency)}</span></div>
          </div>
        </div>

        {/* Terms */}
        {(q.terms || []).length > 0 && (
          <div className="mb-8">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Terms &amp; Conditions</div>
            <ol className="text-xs text-slate-600 space-y-1 list-decimal ml-5">
              {q.terms.map((t, i) => <li key={i}>{t}</li>)}
            </ol>
          </div>
        )}

        <div className="text-center text-[10px] text-slate-400 italic">
          {profile.default_tagline ? `~ ${profile.default_tagline} ~` : ""}
        </div>
      </div>

      {/* Accept dialog */}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}>
        <DialogContent className="rounded-sm max-w-md bg-white text-slate-900" data-testid="accept-dialog">
          <DialogHeader><DialogTitle>Accept this quotation?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-slate-600">Once accepted, an invoice for <b>{fmt(q.total, q.currency)}</b> will be issued and emailed to you. Payment options (bank transfer or PayNow) will be on the invoice.</div>
            <div>
              <Label>Your name <span className="text-[10px] text-slate-400 uppercase tracking-widest">— for our records</span></Label>
              <Input className="rounded-sm mt-1.5" value={signerName} onChange={(e) => setSignerName(e.target.value)} data-testid="accept-signer" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setAcceptOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-green-600 hover:bg-green-700 text-white" onClick={doAccept} disabled={busy} data-testid="accept-confirm">
              <Check className="h-4 w-4 mr-2" />{busy ? "Accepting…" : "Yes, accept & issue invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="rounded-sm max-w-md bg-white text-slate-900" data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject this quotation?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-slate-600">If it's a matter of price or timing, we'd love the chance to revise it — you can also just close this tab instead.</div>
            <div>
              <Label>Reason <span className="text-[10px] text-slate-400 uppercase tracking-widest">— optional</span></Label>
              <Textarea rows={3} className="rounded-sm mt-1.5" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700 text-white" onClick={doReject} disabled={busy} data-testid="reject-confirm">
              <X className="h-4 w-4 mr-2" />{busy ? "Rejecting…" : "Reject quotation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
